import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const allComics = getComicsByCategory(slug || '');

  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const sortedComics = useMemo(() => {
    const list = [...allComics];
    switch (sortBy) {
      case 'popular':
        return list.sort((a, b) => b.views - a.views);
      case 'rating':
        return list.sort((a, b) => b.rating - a.rating);
      case 'latest':
      default:
        return list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
  }, [allComics, sortBy]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类' },
              { label: category.name },
            ]}
          />

          <div className="mt-6 mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{category.description}</p>
          </div>

          <div className="flex items-center flex-wrap gap-2 mb-6">
            <span className="text-sm text-foreground-500 mr-2">排序:</span>
            <button
              onClick={() => setSortBy('latest')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                sortBy === 'latest'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              最新更新
            </button>
            <button
              onClick={() => setSortBy('popular')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                sortBy === 'popular'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              最受欢迎
            </button>
            <button
              onClick={() => setSortBy('rating')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                sortBy === 'rating'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              评分最高
            </button>
          </div>

          {sortedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {sortedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-sm">该分类暂无漫画，敬请期待</p>
            </div>
          )}

          <div className="mt-12">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">其他分类</h3>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50/60 transition-colors whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}